import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import CustomButton from '../../components/button/ButtonComponent';
import { PasswordSetup } from '../../components/password-setup/password-setup';
import { colors } from '../../components/theme/theme';
import {
    completeRegistrationInvite,
    CompleteRegistrationInviteDetails,
    getRegistrationInviteByToken,
} from '../../services/admin-registration-services';
import { openToast } from '../../store/reducers/toastSlice';
import { NavigationConfig } from '../../utils/navigation-constants';

type LoadState = 'loading' | 'not-found' | 'expired' | 'already-completed' | 'ready' | 'submitted';

function isAlreadyCompletedMessage(msg: string | null | undefined): boolean {
    if (!msg) return false;
    return msg.toLowerCase().includes('already been completed');
}

function isExpiredMessage(msg: string | null | undefined): boolean {
    if (!msg) return false;
    return msg.toLowerCase().includes('expired');
}

const CompleteRegistration: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token = '' } = useParams<{ token: string }>();

    const [loadState, setLoadState] = useState<LoadState>(() => (token ? 'loading' : 'not-found'));
    const [details, setDetails] = useState<CompleteRegistrationInviteDetails | null>(null);
    const [showPasswordSetup, setShowPasswordSetup] = useState(false);
    const fetchSeqRef = useRef(0);

    useEffect(() => {
        if (!token) {
            return;
        }
        const seq = ++fetchSeqRef.current;
        let cancelled = false;

        (async () => {
            try {
                const res = await getRegistrationInviteByToken(token);
                if (cancelled || seq !== fetchSeqRef.current) return;

                if (res?.statusCode === 200) {
                    setDetails((res.data as CompleteRegistrationInviteDetails) ?? null);
                    setLoadState('ready');
                    return;
                }
                if (res?.statusCode === 404) {
                    setLoadState('not-found');
                    return;
                }
                if (res?.statusCode === 410) {
                    setLoadState(isAlreadyCompletedMessage(res.message) ? 'already-completed' : 'expired');
                    return;
                }
                setLoadState(isExpiredMessage(res?.message) ? 'expired' : 'not-found');
            } catch {
                if (cancelled || seq !== fetchSeqRef.current) return;
                setLoadState('not-found');
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [token]);

    const handlePasswordSetupComplete = async (password: string) => {
        try {
            const res = await completeRegistrationInvite(token, password);
            if (res?.statusCode === 200) {
                setLoadState('submitted');
                setTimeout(() => {
                    navigate(NavigationConfig.Login, { state: { fromCompleteRegistration: true } });
                }, 2000);
                return;
            }
            if (res?.statusCode === 410) {
                setLoadState(isAlreadyCompletedMessage(res.message) ? 'already-completed' : 'expired');
                return;
            }
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: res?.message || 'Unable to complete registration. Please try again.',
                    severity: 'error',
                }),
            );
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message || 'Unable to complete registration. Please try again.';
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: msg,
                    severity: 'error',
                }),
            );
        }
    };

    const detailRows = details
        ? [
              { label: 'Name', value: details.name || [details.firstName, details.lastName].filter(Boolean).join(' ') },
              { label: 'Email', value: details.email },
              { label: 'Phone Number', value: details.phoneNumber },
              { label: 'Contract ID', value: details.contractId },
              {
                  label: 'Address',
                  value: [details.street, details.city, details.state, details.postalCode, details.country]
                      .filter(Boolean)
                      .join(', '),
              },
          ].filter(row => row.value)
        : [];

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                bgcolor: 'rgba(15, 23, 42, 0.4)',
                px: { xs: 1.25, sm: 1.5 },
                py: { xs: 1.5, sm: 2 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
            }}
        >
            {loadState !== 'loading' && (
                <Paper
                    elevation={6}
                    sx={{
                        width: '100%',
                        maxWidth: 480,
                        maxHeight: 'calc(100dvh - 24px)',
                        overflowY: 'auto',
                        borderRadius: 2.5,
                        p: { xs: 2, sm: 3 },
                        boxShadow: '0 16px 40px rgba(0, 0, 0, 0.14)',
                    }}
                >
                    {loadState === 'not-found' && (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            This registration link is invalid. Please contact support for assistance.
                        </Alert>
                    )}

                    {loadState === 'expired' && (
                        <Stack alignItems="center" textAlign="center" spacing={2}>
                            <Typography variant="h6" fontWeight={700}>
                                This Link Has Expired
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please contact support to request a new registration link.
                            </Typography>
                        </Stack>
                    )}

                    {loadState === 'already-completed' && (
                        <Stack alignItems="center" textAlign="center" spacing={2}>
                            <Typography variant="h6" fontWeight={700}>
                                Registration Already Completed
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                This account has already been set up. Please log in to continue.
                            </Typography>
                            <CustomButton onClick={() => navigate(NavigationConfig.Login)}>Go to Login</CustomButton>
                        </Stack>
                    )}

                    {loadState === 'submitted' && (
                        <Stack alignItems="center" textAlign="center" spacing={2}>
                            <Typography variant="h6" fontWeight={700}>
                                Password Set Successfully
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Redirecting you to login…
                            </Typography>
                        </Stack>
                    )}

                    {loadState === 'ready' && !showPasswordSetup && (
                        <Stack spacing={2.5}>
                            <Typography variant="h6" fontWeight={700}>
                                Complete Your Registration
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please review the details below, then set your password to finish setting up your
                                account.
                            </Typography>
                            <Box
                                sx={{
                                    bgcolor: colors.greyPreset[10] ?? '#f7f4ee',
                                    borderRadius: 2,
                                    px: 1.5,
                                    py: 0.25,
                                    border: '1px solid #ebe6dc',
                                }}
                            >
                                {detailRows.map((row, i) => (
                                    <Stack
                                        key={row.label}
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        gap={1.5}
                                        sx={{
                                            py: 1,
                                            borderBottom: i < detailRows.length - 1 ? '1px solid #e8e4db' : 'none',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                            {row.label}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            textAlign="right"
                                            sx={{ fontSize: '0.8rem', wordBreak: 'break-word' }}
                                        >
                                            {row.value}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>
                            <CustomButton onClick={() => setShowPasswordSetup(true)}>Reset Password</CustomButton>
                        </Stack>
                    )}

                    {loadState === 'ready' && showPasswordSetup && (
                        <PasswordSetup onPasswordSetupComplete={handlePasswordSetupComplete} />
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default CompleteRegistration;
